import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import api from '../services/api';
import { useCreateProduct } from '../hooks/useProducts';
import { useState } from 'react';

interface RecommendedProduct {
  id: string;
  title: string | null;
  retailer: string | null;
  price: number | null;
  currency: string | null;
  imageUrl: string | null;
  url: string | null;
}

function useRecommendations(productId: string) {
  return useQuery({
    queryKey: ['recommendations', productId],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: RecommendedProduct[] }>(
        `/products/${productId}/recommendations`,
        { params: { limit: 8 } }
      );
      return res.data.data;
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });
}

export default function SimilarProducts({ productId }: { productId: string }) {
  const { data: recommendations, isLoading } = useRecommendations(productId);
  const createProduct = useCreateProduct();
  const [trackedIds, setTrackedIds] = useState<Set<string>>(new Set());
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Finding similar products...
      </div>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return null; // Don't show section if no recommendations
  }

  function handleTrack(product: RecommendedProduct) {
    if (!product.url) return;
    setPendingId(product.id);
    createProduct.mutate(
      {
        name: product.title || 'Untitled Product',
        normalized_url: product.url,
      },
      {
        onSuccess: () => setTrackedIds((prev) => new Set(prev).add(product.id)),
        onSettled: () => setPendingId(null),
      }
    );
  }

  function formatPrice(price: number | null, currency: string | null) {
    if (price == null) return null;
    const sym = currency === 'USD' || !currency ? '$' : currency + ' ';
    return `${sym}${price.toFixed(2)}`;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Similar Products
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {recommendations.map((product) => {
          const isTracked = trackedIds.has(product.id);
          const isPending = pendingId === product.id;

          return (
            <div
              key={product.id}
              className="rounded-lg border border-border bg-card overflow-hidden hover:shadow-sm transition-shadow"
            >
              {product.imageUrl ? (
                <div className="aspect-square bg-muted">
                  <img
                    src={product.imageUrl}
                    alt={product.title || ''}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="aspect-square bg-muted flex items-center justify-center">
                  <span className="text-muted-foreground text-xs">No image</span>
                </div>
              )}
              <div className="p-2 space-y-1">
                <p className="text-xs font-medium text-card-foreground line-clamp-2 leading-tight">
                  {product.title || 'Untitled'}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground truncate">
                    {product.retailer}
                  </span>
                  {product.price != null && (
                    <span className="text-xs font-semibold text-foreground">
                      {formatPrice(product.price, product.currency)}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleTrack(product)}
                  disabled={isPending || isTracked || !product.url}
                  className="w-full inline-flex items-center justify-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                >
                  {isTracked ? (
                    'Tracked'
                  ) : isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-3 w-3" />
                      Track
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
